import React, { useContext, useEffect, useRef, useState } from 'react'
import { backendStatus } from '../../../../../../backend/backend'
import Checkbox from '../../../../../../components/checkbox/checkbox'
import Form from '../../../../../../components/form/form'
import InfoPopup from '../../../../../../components/info-popup/info-popup'
import Loading from '../../../../../../components/loading/loading'
import DefaultStatusModals from '../../../../../../components/modal/default-status-modals'
import Select from '../../../../../../components/select/select'
import { DeviceContext } from '../../manage-page'
import routerImage from '../../../../../../images/router.png'

import './inmesh.css'

const RETRY_TIME = 5000

const EASYMESH_TYPE = {
    CONTROLLER: 0,
    AGENT: 1,
}

const EASYMESH_BLOCK_WAN_NOT_SUPPORTED = 2
// WAN port blocking is hidden from the UI for now; set to true to expose it again.
const BLOCK_WAN_PORT_UI_ENABLED = false
const SYSTEM_MODE_ROUTER = 0

const typeLabels = {
    [EASYMESH_TYPE.CONTROLLER]: 'Nó principal',
    [EASYMESH_TYPE.AGENT]: 'Nó secundário',
}

const backhaulLabels = {
    0: 'Automático',
    1: '2,4 GHz',
    2: '5 GHz',
    3: 'Ethernet',
}

const syncingLabels = {
    0: 'Parada',
    1: 'Em andamento',
    2: 'Concluída',
    3: 'Sobreposição detectada',
    4: 'Erro',
}

const valueOrUnavailable = (value) => {
    if (value === undefined || value === null || value === '') return 'Não informado'

    return value
}

const getBackhaulLabel = (backhaul) => {
    if (backhaul === undefined || backhaul === null) return 'Não informado'

    return backhaulLabels[backhaul] || String(backhaul)
}

const getResourceContent = (content) => Array.isArray(content) ? content[0] : content

const getNodeMac = (node) => node.lanMac || node.lan_mac || node.mac

const getNodeLabel = (name, model, lanMac) => {
    const lines = [valueOrUnavailable(name || model)]

    if (model && model !== name) lines.push(model)

    lines.push(`MAC da LAN: ${valueOrUnavailable(lanMac)}`)
    return lines.join('\n')
}

const getNodeTitle = (node, root, controllerModel) => {
    const details = [
        `Modelo: ${valueOrUnavailable(root ? controllerModel : node.model)}`,
        `IP: ${valueOrUnavailable(root ? node.controllerIp : node.ip)}`,
    ]

    if (!root) {
        details.unshift(`Nome: ${valueOrUnavailable(node.name || node.model)}`)
        details.push(`MAC da LAN: ${valueOrUnavailable(getNodeMac(node))}`)
        if (typeof node.is_online === 'boolean') {
            details.splice(2, 0, `Status: ${node.is_online ? 'Online' : 'Offline'}`)
        }
    }

    return details.join('\n')
}

const getTopology = (mesh, device) => {
    const controllerModel = device.data.model || 'Dispositivo principal'
    const nodes = [{
        id: 'controller',
        label: controllerModel,
        shape: 'image',
        image: routerImage,
        brokenImage: routerImage,
        size: 45,
        font: { align: 'center' },
        title: getNodeTitle(mesh, true, controllerModel),
    }]
    const edges = []
    const nodeMacs = {}

    const appendNeighbors = (neighbors, parentId, path) => {
        if (!Array.isArray(neighbors)) return

        neighbors.forEach((neighbor, index) => {
            const id = `${path}-${index}`
            const online = neighbor.is_online !== false

            nodes.push({
                id,
                label: getNodeLabel(neighbor.name || neighbor.model || neighbor.mac || 'Dispositivo InMesh', neighbor.model, getNodeMac(neighbor)),
                shape: 'image',
                image: routerImage,
                brokenImage: routerImage,
                size: 40,
                font: { align: 'center' },
                color: online ? undefined : { opacity: 0.35 },
                title: getNodeTitle(neighbor, false, controllerModel),
            })
            nodeMacs[id] = getNodeMac(neighbor)
            edges.push({
                from: parentId,
                to: id,
                label: getBackhaulLabel(neighbor.backhaul),
                font: { align: 'top' },
                color: online ? '#424242' : '#9e9e9e',
                dashes: !online,
            })

            appendNeighbors(neighbor.neighbors, id, id)
        })
    }

    appendNeighbors(mesh.neighbors, 'controller', 'neighbor')

    return { nodes, edges, nodeMacs }
}

function MeshDetails({ mesh, showBlockWanPort }) {
    return <div className='easymesh-details'>
        <div><span>Status</span><strong>{mesh.enabled ? 'Ativado' : 'Desativado'}</strong></div>
        <div><span>Tipo</span><strong>{typeLabels[mesh.type] || valueOrUnavailable(mesh.type)}</strong></div>
        <div><span>IP do controller</span><strong>{valueOrUnavailable(mesh.controllerIp)}</strong></div>
        <div><span>Sincronização</span><strong>{syncingLabels[mesh.syncing] || valueOrUnavailable(mesh.syncing)}</strong></div>
        {showBlockWanPort && <div><span>Bloqueio da WAN</span><strong>{mesh.blockWanPort ? 'Ativo' : 'Inativo'}</strong></div>}
    </div>
}

function EasyMeshConfiguration({ mesh, setMesh, disabled, showBlockWanPort }) {
    return <div className='easymesh-configuration'>
        <div className='subtitle'>Configurações da rede InMesh</div>

        <div className='easymesh-enable'>
            <Checkbox
                id='easymesh-enabled'
                label='Habilitar InMesh'
                value={mesh.enabled}
                disabled={disabled}
                toggleFn={() => setMesh({ ...mesh, enabled: !mesh.enabled })}
            ></Checkbox>
            <InfoPopup message='Para utilizar InMesh, mantenha as redes de 2,4 GHz e 5 GHz com o mesmo SSID e segurança WPA2.'></InfoPopup>
        </div>

        {mesh.enabled && <div className='easymesh-fields'>
            <Select
                id='easymesh-type'
                name='easymesh-type'
                label='Modo de operação'
                value={mesh.type}
                onChange={(event) => setMesh({ ...mesh, type: Number(event.target.value) })}
                options={[
                    { value: EASYMESH_TYPE.CONTROLLER, text: 'Nó principal' },
                    { value: EASYMESH_TYPE.AGENT, text: 'Nó secundário' },
                ]}
            ></Select>

            {showBlockWanPort && <Checkbox
                id='easymesh-block-wan'
                label='Bloquear porta WAN'
                value={mesh.blockWanPort === 1}
                disabled={disabled}
                toggleFn={() => setMesh({ ...mesh, blockWanPort: mesh.blockWanPort === 1 ? 0 : 1 })}
            ></Checkbox>}
        </div>}
    </div>
}

export default function EasyMeshManagementPage() {
    const device = useContext(DeviceContext)
    const [mesh, setMesh] = useState(null)
    const [system, setSystem] = useState(null)
    const [loadError, setLoadError] = useState(false)
    const [saving, setSaving] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState(false)
    const retryTimer = useRef(null)
    const copyFeedbackTimer = useRef(null)
    const mounted = useRef(true)
    const networkContainer = useRef(null)
    const network = useRef(null)
    const [copiedLanMac, setCopiedLanMac] = useState('')

    const fetchMesh = async () => {
        const result = await device.retrieveResource('easymesh')

        if (result.status !== backendStatus.SUCCESS) {
            if (mounted.current) {
                setLoadError(true)
                retryTimer.current = setTimeout(fetchMesh, RETRY_TIME)
            }
            return
        }

        const content = getResourceContent(result.content)
        if (!content) {
            if (mounted.current) setLoadError(true)
            return
        }

        if (mounted.current) {
            setLoadError(false)
            setMesh(content)
        }
    }

    const fetchSystem = async () => {
        const result = await device.retrieveResource('system')
        if (result.status === backendStatus.SUCCESS && mounted.current) {
            setSystem(getResourceContent(result.content))
        }
    }

    useEffect(() => {
        fetchMesh()
        fetchSystem()

        return () => {
            mounted.current = false
            clearTimeout(retryTimer.current)
            clearTimeout(copyFeedbackTimer.current)
            if (network.current) network.current.destroy()
        }

        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (!mesh || !mesh.enabled || !networkContainer.current) return

        const vis = require('vis-network/standalone')
        const topology = getTopology(mesh, device)
        const data = {
            nodes: new vis.DataSet(topology.nodes),
            edges: new vis.DataSet(topology.edges),
        }
        const options = {
            interaction: { dragNodes: false, dragView: false, zoomView: false, selectable: false },
            layout: {
                hierarchical: {
                    enabled: true,
                    direction: 'LR',
                    sortMethod: 'directed',
                    levelSeparation: 260,
                    nodeSpacing: 180,
                },
            },
            physics: false,
        }

        if (network.current) network.current.destroy()
        network.current = new vis.Network(networkContainer.current, data, options)
        network.current.on('click', async (params) => {
            const nodeId = params.nodes[0] || network.current.getNodeAt(params.pointer.DOM)
            const lanMac = topology.nodeMacs[nodeId]
            if (!lanMac) return

            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(lanMac)
                } else {
                    throw new Error('Clipboard API indisponível')
                }
            } catch (clipboardError) {
                const textArea = document.createElement('textarea')
                textArea.value = lanMac
                document.body.appendChild(textArea)
                textArea.select()
                const copied = document.execCommand('copy')
                document.body.removeChild(textArea)
                if (!copied) {
                    console.error('Não foi possível copiar o MAC da LAN', clipboardError)
                    return
                }
            }

            if (mounted.current) {
                setCopiedLanMac(lanMac)
                clearTimeout(copyFeedbackTimer.current)
                copyFeedbackTimer.current = setTimeout(() => setCopiedLanMac(''), 2000)
            }
        })

        return () => {
            if (network.current) {
                network.current.destroy()
                network.current = null
            }
        }
    }, [mesh, device])

    const isRouterMode = system && Number(system.operationMode) === SYSTEM_MODE_ROUTER
    const showBlockWanPort = BLOCK_WAN_PORT_UI_ENABLED && Boolean(system) && !isRouterMode && mesh && mesh.blockWanPort !== EASYMESH_BLOCK_WAN_NOT_SUPPORTED

    const saveMesh = async () => {
        setSaving(true)

        // The EasyMesh translator requires these fields and rejects `syncing`.
        const update = {
            id: mesh.id || '0',
            enabled: Boolean(mesh.enabled),
            controllerIp: mesh.controllerIp || '',
            type: Number(mesh.type),
            backhaul: Number(mesh.backhaul),
        }

        if (showBlockWanPort) {
            update.blockWanPort = Number(mesh.blockWanPort)
        }

        let result = await device.updateResource('easymesh', update)
        if (!result || result.status !== backendStatus.SUCCESS) {
            setSaving(false)
            setError(true)
            return
        }

        result = await device.apply()
        if (!result || result.status !== backendStatus.SUCCESS) {
            setSaving(false)
            setError(true)
            return
        }

        setSaving(false)
        setSuccess(true)
        fetchMesh()
    }

    const dismissModal = () => {
        setSaving(false)
        setSuccess(false)
        setError(false)
    }

    return <div className='easymesh-management-page'>
        <div className='subtitle'>InMesh</div>

        {!mesh && !loadError && <Loading show={true}></Loading>}

        {loadError && !mesh && <div className='easymesh-message'>
            Não foi possível obter os dados InMesh deste dispositivo.
        </div>}

        {mesh && <React.Fragment>
            <MeshDetails mesh={mesh} showBlockWanPort={showBlockWanPort}></MeshDetails>

            <Form id='easymesh-configuration-form' onSubmit={saveMesh} disableButton={saving}>
                <EasyMeshConfiguration mesh={mesh} setMesh={setMesh} disabled={saving} showBlockWanPort={showBlockWanPort}></EasyMeshConfiguration>
            </Form>

            {!mesh.enabled && <div className='easymesh-message'>
                O InMesh está desativado neste dispositivo.
            </div>}

            {mesh.enabled && <React.Fragment>
                <div className='easymesh-topology-title'>Topologia da rede</div>
                <div className='easymesh-topology-hint'>Clique no MAC da LAN de um equipamento para copiá-lo.</div>
                <div className='easymesh-topology' ref={networkContainer}></div>
                {copiedLanMac && <div className='easymesh-copy-feedback' role='status'>MAC da LAN copiado: {copiedLanMac}</div>}
            </React.Fragment>}
        </React.Fragment>}

        <DefaultStatusModals
            saving={saving}
            success={success}
            error={error}
            continueFn={dismissModal}
        ></DefaultStatusModals>
    </div>
}
